import { ImgHTMLAttributes, useEffect, useState } from "react";

interface FreezeGifProps extends Omit<ImgHTMLAttributes<HTMLImageElement>, "src"> {
    src: string;
    shouldAnimate?: boolean;
    fallbackSrc?: string;
}

export const canAnimateProfilePhoto = (stamps?: readonly string[] | null) =>
    Boolean(stamps?.includes("blue") || stamps?.includes("brown"));

/**
 * Exibe a imagem normalmente apenas para quem pode usar avatar animado.
 * Para os demais usuários, desenha o primeiro frame em um canvas e usa uma
 * imagem PNG estática, independentemente da extensão salva no bucket.
 */
export const FreezeGif = ({
    src,
    shouldAnimate = false,
    fallbackSrc = "/img/img-account.png",
    onError,
    style,
    ...props
}: FreezeGifProps) => {
    const [staticSrc, setStaticSrc] = useState<string | null>(shouldAnimate ? src : null);
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        let active = true;

        setFailed(false);

        if (shouldAnimate) {
            setStaticSrc(src);
            return () => {
                active = false;
            };
        }

        setStaticSrc(null);

        const img = new Image();
        img.crossOrigin = "anonymous";

        img.onload = () => {
            if (!active) return;

            try {
                const canvas = document.createElement("canvas");
                canvas.width = img.naturalWidth || img.width;
                canvas.height = img.naturalHeight || img.height;

                const ctx = canvas.getContext("2d");
                if (!ctx || !canvas.width || !canvas.height) throw new Error("Não foi possível congelar a imagem");

                ctx.drawImage(img, 0, 0);
                setStaticSrc(canvas.toDataURL("image/png"));
            } catch {
                setFailed(true);
            }
        };

        img.onerror = () => {
            if (active) setFailed(true);
        };

        img.src = src;

        return () => {
            active = false;
            img.onload = null;
            img.onerror = null;
        };
    }, [src, shouldAnimate]);

    const resolvedSrc = failed ? fallbackSrc : staticSrc;

    return (
        <img
            {...props}
            src={resolvedSrc ?? fallbackSrc}
            style={style}
            onError={onError}
        />
    );
};
